import { useState, useEffect, useRef } from 'react';
import { Upload, Copy, Trash2, Check, ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyImageGallery = ({ token }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const fileRef = useRef(null);

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/imgtool/my-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(res.data.images || []);
    } catch (e) {
      console.error('Error al cargar galería', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('La imagen debe ser menor a 8MB'); return; }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { toast.error('Usa JPG, PNG, GIF o WEBP'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      await axios.post(`${BACKEND_URL}/api/imgtool/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Imagen subida a tu galería');
      await fetchImages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir la imagen');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const copyUrl = (img) => {
    navigator.clipboard.writeText(img.url);
    setCopiedId(img.id);
    toast.success('Enlace copiado');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const deleteImage = async (img) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/imgtool/my-images/${img.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages((prev) => prev.filter((i) => i.id !== img.id));
      toast.success('Imagen quitada de tu galería');
    } catch (err) {
      toast.error('No se pudo quitar la imagen');
    }
  };

  return (
    <div className="space-y-6" data-testid="my-image-gallery">
      <div className="bg-gradient-to-r from-violet-600/20 to-purple-600/10 border border-violet-500/30 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <ImageIcon className="w-6 h-6 text-violet-300" />
          <h2 className="gotham-bold text-xl text-white">Mi Galería de Imágenes</h2>
        </div>
        <p className="text-white/60 text-sm">
          Sube y reutiliza tus imágenes (alojadas de forma permanente en Viubux ImgTool). Solo tú ves tus imágenes; nadie más tiene acceso a ellas.
        </p>
        <div className="mt-4">
          <label className="inline-flex">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" disabled={uploading} onChange={handleUpload} data-testid="gallery-upload-input" />
            <Button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={uploading}
              className="bg-violet-500 hover:bg-violet-600 text-white gotham-bold"
              data-testid="gallery-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Subiendo...' : 'Subir imagen'}
            </Button>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-white/10 rounded-2xl">
          <ImageIcon className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/50 text-sm">Aún no has subido imágenes. Sube tu primera imagen para verla aquí.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {images.map((img) => (
            <div key={img.id} className="group relative rounded-xl overflow-hidden border border-white/10 bg-white/5" data-testid={`gallery-item-${img.id}`}>
              <div className="aspect-square bg-black/30">
                <img src={img.url} alt={img.filename || 'imagen'} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-2 flex gap-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => copyUrl(img)}
                  className="flex-1 flex items-center justify-center gap-1 bg-white/90 hover:bg-white text-violet-700 rounded-lg py-1.5 text-xs gotham-medium transition-colors"
                  data-testid={`gallery-copy-${img.id}`}
                >
                  {copiedId === img.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedId === img.id ? 'Copiado' : 'Copiar'}
                </button>
                <button
                  onClick={() => deleteImage(img)}
                  className="flex items-center justify-center bg-red-500/90 hover:bg-red-600 text-white rounded-lg p-1.5 transition-colors"
                  title="Quitar de mi galería"
                  data-testid={`gallery-delete-${img.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyImageGallery;
